import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Link, Typography } from '@mui/material';
// hooks
import useAuth from '../../../hooks/useAuth';
// routes
import {
  PATH_CLIENT,
  PATH_DASHBOARD,
  PATH_LINGUIST
} from '../../../routes/paths';
// components
import MyAvatar from '../../../components/MyAvatar';
//
import { SIDEBAR_FONT } from './sidebarTokens';

// ----------------------------------------------------------------------
// Who is signed in, as a card at the top of the rail. The avatar wears the
// portal's gradient as a ring — the one place the full gradient appears in
// the sidebar, so it stays a mark of identity rather than decoration.
// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5, 1.5),
  borderRadius: 14,
  backgroundColor: 'var(--sb-card)',
  border: '1px solid var(--sb-glass-border)',
  transition: theme.transitions.create(
    ['background-color', 'border-color', 'padding'],
    { duration: theme.transitions.duration.shorter }
  ),
  '&:hover': {
    backgroundColor: 'var(--sb-card-hover)',
    borderColor: 'var(--sb-glass-border-strong)'
  }
}));

// ----------------------------------------------------------------------

NavbarAccount.propTypes = {
  isCollapse: PropTypes.bool
};

export default function NavbarAccount({ isCollapse }) {
  const { user, type } = useAuth();

  const name =
    user?.name ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.email;

  // What sits under the name: the job title for staff, the company for a
  // client, and the account's role for everyone on the linguist portal.
  const subtitle =
    type === 'user'
      ? user?.jobTitle
      : type === 'Client'
      ? user?.companyName
      : user?.type;

  return (
    <Link
      underline="none"
      color="inherit"
      component={RouterLink}
      to={
        user?.type === 'Linguist'
          ? PATH_LINGUIST.user.editProfile
          : user?.type === 'Client'
          ? PATH_CLIENT.client.editProfile
          : PATH_DASHBOARD.user.account
      }
      sx={{ display: 'block' }}
    >
      <RootStyle
        sx={{
          ...(isCollapse && {
            p: 0.5,
            bgcolor: 'transparent',
            borderColor: 'transparent',
            justifyContent: 'center'
          })
        }}
      >
        {/* Gradient ring: a 2px band of the portal gradient behind a
            ground-coloured gap, so the ring reads as light around the
            photo rather than a border glued to it. */}
        <Box
          sx={{
            flexShrink: 0,
            p: '2px',
            borderRadius: '50%',
            background: 'var(--sb-gradient)',
            boxShadow: '0 6px 18px var(--sb-glow)'
          }}
        >
          <Box
            sx={{
              p: '2px',
              borderRadius: '50%',
              bgcolor: 'var(--sb-ground)',
              lineHeight: 0
            }}
          >
            <MyAvatar sx={{ width: 36, height: 36 }} />
          </Box>
        </Box>

        <Box
          sx={{
            ml: 1.5,
            minWidth: 0,
            transition: (theme) =>
              theme.transitions.create(['width', 'opacity'], {
                duration: theme.transitions.duration.shorter
              }),
            ...(isCollapse && {
              ml: 0,
              width: 0,
              opacity: 0
            })
          }}
        >
          <Typography
            noWrap
            sx={{
              fontFamily: SIDEBAR_FONT,
              fontWeight: 600,
              fontSize: '0.875rem',
              lineHeight: 1.3,
              color: 'var(--sb-text)'
            }}
          >
            {name}
          </Typography>
          {subtitle && (
            <Typography
              noWrap
              sx={{
                mt: 0.25,
                fontFamily: SIDEBAR_FONT,
                fontWeight: 500,
                fontSize: '0.75rem',
                lineHeight: 1.3,
                color: 'var(--sb-text-soft)'
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </RootStyle>
    </Link>
  );
}
